import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import {
  composeStudentDisplayNameFromParts,
  normalizeStudentNamePart,
  type AdminGuardianLinkDto,
  type AdminStudentSelfInviteDto,
  type AdminStudentDto,
  type CreateAdminStudentSelfInviteResponseDto,
  type FamilyAccountsResponseDto,
  type StudentStatus,
  type UpdateAdminStudentRequestDto
} from "@madamuh/domain";
import { useNavigate, useParams } from "react-router-dom";
import {
  createAdminStudentSelfInvite,
  fetchAdminStudentSelfInvites,
  revokeAdminStudentSelfInvite
} from "../../shared/api/adminDirectory";
import {
  fetchAdminStudent,
  fetchFamilyAccounts,
  patchAdminStudent
} from "../../shared/api/adminRoster";
import {
  fetchAdminGuardianLinksForStudent,
  revokeAdminGuardianLink
} from "../../shared/api/guardian";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import {
  DEFAULT_TENANT_TIMEZONE,
  formatTenantIsoDateTime,
  isoToTenantLocalDateTimeInput,
  tenantLocalDateTimeToIso
} from "../../shared/lib/tenantDateTime";
import { resolveInviteTenantName } from "../../shared/lib/inviteShare";
import { describeRelationType, describeStatus } from "../../shared/lib/uiLabels";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { InviteSharePanel } from "../../shared/ui/InviteSharePanel";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";

const adminLinks = [
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" },
  { to: "/t/:tenantSlug/teacher/admin/students", label: "生徒一覧" },
  { to: "/t/:tenantSlug/teacher/admin/enrollments", label: "所属" }
];

type StudentEditFormState = {
  familyName: string;
  givenName: string;
  legacyDisplayName: string;
  displayNameKana: string;
  birthDate: string;
  status: StudentStatus;
  familyAccountId: string;
  notesInternal: string;
};

function createFormState(student: AdminStudentDto): StudentEditFormState {
  return {
    familyName: student.familyName ?? "",
    givenName: student.givenName ?? "",
    legacyDisplayName: student.displayName,
    displayNameKana: student.displayNameKana ?? "",
    birthDate: student.birthDate,
    status: student.status,
    familyAccountId: student.familyAccountId ?? "",
    notesInternal: student.notesInternal ?? ""
  };
}

function getStudentDisplayNamePreview(form: StudentEditFormState, fallbackDisplayName: string): string {
  return composeStudentDisplayNameFromParts(form.familyName, form.givenName) ?? fallbackDisplayName;
}

function buildPatchRequest(
  form: StudentEditFormState,
  student: AdminStudentDto
): UpdateAdminStudentRequestDto {
  const familyName = normalizeStudentNamePart(form.familyName);
  const givenName = normalizeStudentNamePart(form.givenName);
  const request: UpdateAdminStudentRequestDto = {
    displayNameKana: form.displayNameKana.trim() || null,
    birthDate: form.birthDate,
    status: form.status,
    familyAccountId: form.familyAccountId || null,
    notesInternal: form.notesInternal.trim() || null
  };

  const originalFamilyName = student.familyName ?? null;
  const originalGivenName = student.givenName ?? null;
  const hasNamePartChange = familyName !== originalFamilyName || givenName !== originalGivenName;

  if (hasNamePartChange) {
    request.familyName = familyName;
    request.givenName = givenName;
    request.displayName = composeStudentDisplayNameFromParts(familyName, givenName) ?? student.displayName;
  } else if (!familyName && !givenName && form.legacyDisplayName.trim() !== student.displayName) {
    request.displayName = form.legacyDisplayName.trim();
  }

  return request;
}

function createDefaultInviteExpiryInput(now = new Date()): string {
  const next = new Date(now);
  next.setDate(next.getDate() + 14);
  next.setSeconds(0, 0);
  return isoToTenantLocalDateTimeInput(next.toISOString(), DEFAULT_TENANT_TIMEZONE);
}

function describeGuardianLinkSource(link: AdminGuardianLinkDto): string {
  if (link.sourceType === "invite") {
    return link.sourceInviteId ? `招待 ${link.sourceInviteId}` : "招待";
  }

  return link.sourceRequestId ? `申請承認 ${link.sourceRequestId}` : "申請承認";
}

function extractTokenFromInviteUrl(inviteUrl: string): string | null {
  try {
    const parsedUrl =
      typeof window !== "undefined" ? new URL(inviteUrl, window.location.origin) : new URL(inviteUrl);
    const token = parsedUrl.searchParams.get("token")?.trim() ?? "";
    return token.length > 0 ? token : null;
  } catch {
    return null;
  }
}

function buildStudentInviteWebFallbackUrl(tenantSlug: string, inviteUrl: string): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  const token = extractTokenFromInviteUrl(inviteUrl);

  if (!token) {
    return null;
  }

  return `${window.location.origin}/t/${tenantSlug}/student-invite?token=${encodeURIComponent(token)}`;
}

export function AdminStudentDetailPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant", studentId = "" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [student, setStudent] = useState<AdminStudentDto | null>(null);
  const [guardianLinks, setGuardianLinks] = useState<AdminGuardianLinkDto[]>([]);
  const [studentSelfInvites, setStudentSelfInvites] = useState<AdminStudentSelfInviteDto[]>([]);
  const [familyAccounts, setFamilyAccounts] = useState<FamilyAccountsResponseDto | null>(null);
  const [form, setForm] = useState<StudentEditFormState | null>(null);
  const [studentSelfInviteExpiresAt, setStudentSelfInviteExpiresAt] = useState(createDefaultInviteExpiryInput());
  const [latestIssuedStudentInvite, setLatestIssuedStudentInvite] = useState<CreateAdminStudentSelfInviteResponseDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const inviteTenantName = resolveInviteTenantName({
    displayName: session?.tenant.displayName,
    tenantSlug
  });
  const latestStudentInviteWebFallbackUrl = latestIssuedStudentInvite
    ? buildStudentInviteWebFallbackUrl(tenantSlug, latestIssuedStudentInvite.delivery.inviteUrl)
    : null;

  async function refreshPage() {
    setLoadState("loading");
    setLoadError(null);

    try {
      const [nextStudent, nextFamilyAccounts, nextStudentSelfInvites] = await Promise.all([
        fetchAdminStudent(tenantSlug, studentId),
        fetchFamilyAccounts(tenantSlug),
        fetchAdminStudentSelfInvites(tenantSlug)
      ]);
      const nextGuardianLinks = await fetchAdminGuardianLinksForStudent(tenantSlug, studentId);

      setStudent(nextStudent);
      setFamilyAccounts(nextFamilyAccounts);
      setGuardianLinks(nextGuardianLinks.items);
      setStudentSelfInvites(nextStudentSelfInvites.items.filter((invite) => invite.studentId === studentId));
      setForm(createFormState(nextStudent));
      setLoadState("idle");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadState("error");
      setLoadError(getProtectedApiErrorMessage(error, "生徒詳細の取得に失敗しました"));
    }
  }

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole || !studentId) {
      return;
    }

    void refreshPage();
  }, [hasAdminRole, isSessionReady, studentId, tenantSlug]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!form || !student) {
      return;
    }

    setBusy(true);
    setLoadError(null);
    setFeedbackMessage(null);

    const familyName = normalizeStudentNamePart(form.familyName);
    const givenName = normalizeStudentNamePart(form.givenName);

    if ((familyName && !givenName) || (!familyName && givenName)) {
      setBusy(false);
      setLoadError("姓と名を分けて保存する場合は、両方を入力してください。");
      return;
    }

    try {
      const updated = await patchAdminStudent(tenantSlug, studentId, buildPatchRequest(form, student));
      setStudent(updated);
      setForm(createFormState(updated));
      setFeedbackMessage("生徒情報を更新しました。");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "生徒情報の更新に失敗しました"));
    } finally {
      setBusy(false);
    }
  }

  async function handleRevokeGuardianLink(link: AdminGuardianLinkDto) {
    const shouldContinue = globalThis.confirm(
      "この操作は保護者の閲覧権限を解除します。FamilyAccount の設定変更だけでは保護者の閲覧権限は解除されません。"
    );

    if (!shouldContinue) {
      return;
    }

    setBusy(true);
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      const revoked = await revokeAdminGuardianLink(tenantSlug, link.linkId);
      setFeedbackMessage(
        `保護者の紐付けを解除しました: ${revoked.guardianDisplayName ?? revoked.platformUserId}`
      );
      await refreshPage();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "保護者紐付けの解除に失敗しました"));
    } finally {
      setBusy(false);
    }
  }

  async function handleCreateStudentSelfInvite() {
    setBusy(true);
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      const created = await createAdminStudentSelfInvite(tenantSlug, {
        studentId,
        expiresAt: studentSelfInviteExpiresAt
          ? tenantLocalDateTimeToIso(studentSelfInviteExpiresAt, DEFAULT_TENANT_TIMEZONE)
          : null,
        maxUseCount: null
      });
      setLatestIssuedStudentInvite(created);
      setFeedbackMessage(`生徒本人用招待を作成しました: ${created.invite.inviteId}`);
      await refreshPage();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "生徒本人用招待の作成に失敗しました"));
    } finally {
      setBusy(false);
    }
  }

  async function handleRevokeStudentSelfInvite(inviteId: string) {
    setBusy(true);
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      const revoked = await revokeAdminStudentSelfInvite(tenantSlug, inviteId);
      setFeedbackMessage(`生徒本人用招待を失効しました: ${revoked.inviteId}`);
      await refreshPage();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "生徒本人用招待の失効に失敗しました"));
    } finally {
      setBusy(false);
    }
  }

  const activeGuardianLinks = guardianLinks.filter((link) => link.status === "active");
  const revokedGuardianLinks = guardianLinks.filter((link) => link.status !== "active");

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title="生徒詳細"
      description="生徒情報、FamilyAccount、保護者紐付け、生徒本人用招待を管理します。"
      links={adminLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p>利用開始へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は管理者のみ利用できます。</p> : null}
        {loadState === "loading" ? <p>生徒詳細を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}
        {feedbackMessage ? <p>{feedbackMessage}</p> : null}

        {isSessionReady && hasAdminRole && student && form ? (
          <>
            <section style={{ display: "grid", gap: 6 }}>
              <strong>{student.displayName}</strong>
              <span style={{ fontSize: 14, opacity: 0.8 }}>
                {student.memberCode} / {student.studentId}
              </span>
              <span style={{ fontSize: 14, opacity: 0.8 }}>
                FamilyAccount: {student.familyAccountDisplayName ?? "未設定"}
              </span>
            </section>

            <form onSubmit={(event) => void handleSubmit(event)} style={{ display: "grid", gap: 12 }}>
              <label style={{ display: "grid", gap: 4 }}>
                <span>姓</span>
                <input
                  value={form.familyName}
                  onChange={(event) =>
                    setForm((current) => (current ? { ...current, familyName: event.target.value } : current))
                  }
                />
              </label>

              <label style={{ display: "grid", gap: 4 }}>
                <span>名</span>
                <input
                  value={form.givenName}
                  onChange={(event) =>
                    setForm((current) => (current ? { ...current, givenName: event.target.value } : current))
                  }
                />
              </label>

              <div style={{ display: "grid", gap: 4 }}>
                <span>表示名</span>
                <div style={{ padding: "10px 12px", border: "1px solid #e5e5e5", borderRadius: 10 }}>
                  {getStudentDisplayNamePreview(form, student.displayName)}
                </div>
                {!student.familyName || !student.givenName ? (
                  <span style={{ fontSize: 12, opacity: 0.8 }}>
                    既存データは表示名のみです。必要に応じて姓名を入力すると、以後は「姓 名」形式で保存します。
                  </span>
                ) : null}
              </div>

              {!student.familyName && !student.givenName ? (
                <label style={{ display: "grid", gap: 4 }}>
                  <span>既存表示名</span>
                  <input
                    value={form.legacyDisplayName}
                    onChange={(event) =>
                      setForm((current) => (current ? { ...current, legacyDisplayName: event.target.value } : current))
                    }
                  />
                </label>
              ) : null}

              <label style={{ display: "grid", gap: 4 }}>
                <span>表示名かな</span>
                <input
                  value={form.displayNameKana}
                  onChange={(event) =>
                    setForm((current) => (current ? { ...current, displayNameKana: event.target.value } : current))
                  }
                />
              </label>

              <label style={{ display: "grid", gap: 4 }}>
                <span>生年月日</span>
                <input
                  type="date"
                  value={form.birthDate}
                  onChange={(event) => setForm((current) => (current ? { ...current, birthDate: event.target.value } : current))}
                />
              </label>

              <label style={{ display: "grid", gap: 4 }}>
                <span>状態</span>
                <select
                  value={form.status}
                  onChange={(event) =>
                    setForm((current) =>
                      current
                        ? {
                            ...current,
                            status: event.target.value === "inactive" ? "inactive" : "active"
                          }
                        : current
                    )
                  }
                >
                  <option value="active">有効</option>
                  <option value="inactive">無効</option>
                </select>
                <span style={{ fontSize: 12, opacity: 0.8 }}>
                  無効にすると生徒本人・保護者側の表示対象から外れ、本人用の有効リンクや招待も利用停止になります。
                </span>
              </label>

              <label style={{ display: "grid", gap: 4 }}>
                <span>FamilyAccount</span>
                <select
                  value={form.familyAccountId}
                  onChange={(event) =>
                    setForm((current) => (current ? { ...current, familyAccountId: event.target.value } : current))
                  }
                >
                  <option value="">未設定</option>
                  {familyAccounts?.items.map((familyAccount) => (
                    <option key={familyAccount.familyAccountId} value={familyAccount.familyAccountId}>
                      {familyAccount.displayName} / {familyAccount.familyAccountId}
                    </option>
                  ))}
                </select>
                <span style={{ fontSize: 12, opacity: 0.8 }}>
                  FamilyAccount の代表保護者は未設定でもよく、招待消費または承認時に自動設定できます。既存の代表保護者は自動で上書きされません。
                </span>
                <span style={{ fontSize: 12, opacity: 0.8 }}>
                  FamilyAccount は兄弟姉妹・家族グループの設定です。保護者の閲覧権限を解除するには、保護者紐付けを解除してください。
                </span>
              </label>

              <label style={{ display: "grid", gap: 4 }}>
                <span>内部メモ</span>
                <textarea
                  value={form.notesInternal}
                  onChange={(event) =>
                    setForm((current) => (current ? { ...current, notesInternal: event.target.value } : current))
                  }
                  rows={4}
                />
              </label>

              <button type="submit" disabled={busy}>
                {busy ? "保存中..." : "生徒情報を更新する"}
              </button>
            </form>

            <section
              style={{
                display: "grid",
                gap: 12,
                padding: 12,
                border: "1px solid #e5e5e5",
                borderRadius: 12
              }}
            >
              <div style={{ display: "grid", gap: 4 }}>
                <h2 style={{ margin: 0, fontSize: 18 }}>保護者紐付け</h2>
                <p style={{ margin: 0, fontSize: 13, opacity: 0.8 }}>
                  FamilyAccount の設定変更だけでは保護者の閲覧権限は解除されません。閲覧権限は userStudentLinks が管理しています。
                </p>
              </div>

              {activeGuardianLinks.length === 0 ? (
                <p style={{ margin: 0 }}>有効な保護者紐付けはありません。</p>
              ) : (
                <div style={{ display: "grid", gap: 12 }}>
                  {activeGuardianLinks.map((link) => (
                    <article
                      key={link.linkId}
                      style={{
                        display: "grid",
                        gap: 6,
                        padding: 12,
                        border: "1px solid #e5e5e5",
                        borderRadius: 12
                      }}
                    >
                      <strong>{link.guardianDisplayName ?? link.platformUserId}</strong>
                      <span style={{ fontSize: 13, opacity: 0.8 }}>platformUserId: {link.platformUserId}</span>
                      <span style={{ fontSize: 13, opacity: 0.8 }}>
                        続柄: {describeRelationType(link.relationType)} / 状態: {describeStatus(link.status)}
                      </span>
                      <span style={{ fontSize: 13, opacity: 0.8 }}>
                        作成元: {describeGuardianLinkSource(link)} / 作成日時: {formatTenantIsoDateTime(link.createdAt, DEFAULT_TENANT_TIMEZONE)}
                      </span>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void handleRevokeGuardianLink(link)}
                      >
                        保護者の紐付けを解除
                      </button>
                    </article>
                  ))}
                </div>
              )}

              {revokedGuardianLinks.length > 0 ? (
                <div style={{ display: "grid", gap: 8 }}>
                  <strong>解除済み履歴</strong>
                  {revokedGuardianLinks.map((link) => (
                    <div
                      key={link.linkId}
                      style={{
                        display: "grid",
                        gap: 4,
                        padding: 10,
                        border: "1px solid #f0f0f0",
                        borderRadius: 12,
                        opacity: 0.8
                      }}
                    >
                      <span>
                        {link.guardianDisplayName ?? link.platformUserId} / {describeRelationType(link.relationType)}
                      </span>
                      <span style={{ fontSize: 13 }}>
                        {describeGuardianLinkSource(link)} / 解除日時: {formatTenantIsoDateTime(link.revokedAt, DEFAULT_TENANT_TIMEZONE)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>生徒本人用招待</h2>
              <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
                生徒本人が自分の LINE アカウントで予定や動画を見るための招待です。FamilyAccount は不要で、保護者の代表連絡先も変更しません。
              </p>

              <div
                style={{
                  display: "grid",
                  gap: 12,
                  padding: 12,
                  border: "1px solid #e5e5e5",
                  borderRadius: 12
                }}
              >
                <label style={{ display: "grid", gap: 4 }}>
                  <span>有効期限</span>
                  <input
                    type="datetime-local"
                    value={studentSelfInviteExpiresAt}
                    onChange={(event) => setStudentSelfInviteExpiresAt(event.target.value)}
                  />
                </label>
                <button type="button" onClick={() => void handleCreateStudentSelfInvite()} disabled={busy}>
                  {busy ? "作成中..." : "生徒本人用招待を作成"}
                </button>
              </div>

              {latestIssuedStudentInvite ? (
                <InviteSharePanel
                  tenantName={inviteTenantName}
                  inviteKind="studentSelf"
                  primaryUrl={latestIssuedStudentInvite.delivery.inviteUrl}
                  webPreviewUrl={latestStudentInviteWebFallbackUrl}
                  inviteId={latestIssuedStudentInvite.invite.inviteId}
                  onRevoke={() => void handleRevokeStudentSelfInvite(latestIssuedStudentInvite.invite.inviteId)}
                  revokeBusy={busy}
                />
              ) : null}

              {studentSelfInvites.length === 0 ? <p style={{ margin: 0 }}>この生徒の本人用招待はまだありません。</p> : null}
              {studentSelfInvites.map((invite) => (
                <article
                  key={invite.inviteId}
                  style={{
                    border: "1px solid #e5e5e5",
                    borderRadius: 12,
                    padding: 12,
                    display: "grid",
                    gap: 6
                  }}
                >
                  <strong>{invite.studentDisplayName ?? student.displayName}</strong>
                  <div style={{ fontSize: 14, opacity: 0.8 }}>
                    {describeStatus(invite.status)} / 使用回数: {invite.usedCount} / {invite.maxUseCount}
                  </div>
                  <div style={{ fontSize: 14, opacity: 0.8 }}>
                    有効期限: {formatTenantIsoDateTime(invite.expiresAt, DEFAULT_TENANT_TIMEZONE)}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.8 }}>
                    使用日時: {invite.consumedAt ? formatTenantIsoDateTime(invite.consumedAt, DEFAULT_TENANT_TIMEZONE) : "未使用"}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>{invite.inviteId}</div>
                  <button
                    type="button"
                    onClick={() => void handleRevokeStudentSelfInvite(invite.inviteId)}
                    disabled={busy || invite.status !== "active"}
                  >
                    {busy ? "処理中..." : "招待を失効する"}
                  </button>
                </article>
              ))}
            </section>
          </>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
